import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { ChevronDown, CircleCheck, Mail, Wallet } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAppSelector } from '@/app/hooks';
import api, { getErrorMessage } from '@/services/api';
import { ApiResponse, EPaymentMethod, PaymentAccount, TopupRequest, TopupResponse } from '@/services/types';
import { API_ENDPOINTS, MIN_TOPUP_AMOUNT } from '@/utils/constants';
import { Alert, Button, Chip, Input, MethodTile, Sheet, SummaryLine } from '@/components/ui';
import { PageShell } from '@/components/layout/PageShell';
import { formatCurrency } from '@/utils/formatters';
import { PendingPaymentView } from '@/features/checkout/PendingPaymentView';

const VALID_METHODS = new Set(['TRANSFER', 'EPAYMENT', 'FLIP', 'FLIPTRANSFER', 'FLIPVA', 'FLIPEWALLET', 'CASH', 'MIXED']);
const TYPE_MAP: Record<string, string> = { virtual_account: 'FLIPVA', wallet_account: 'FLIPEWALLET', transfer: 'TRANSFER' };

const toTopupMethod = (paymentType: string) => {
    const up = paymentType.toUpperCase();
    return VALID_METHODS.has(up) ? up : TYPE_MAP[paymentType.toLowerCase()] ?? 'FLIPVA';
};
const isAllowed = (m: EPaymentMethod) => VALID_METHODS.has(m.paymentType.toUpperCase()) || m.paymentType.toLowerCase() in TYPE_MAP;
const topupFee = (m: EPaymentMethod | null, amount: number) =>
    !m ? 0 : m.paymentFeeUnit === 'PERCENT' ? Math.ceil((amount * m.paymentFee) / 100) : m.paymentFee;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const QUICK = [100_000, 250_000, 500_000, 1_000_000];
const short = (v: number) => formatCurrency(v).replace(/^Rp\s?/, '');
const feeLabel = (m: EPaymentMethod) => (m.paymentFeeUnit === 'PERCENT' ? `${m.paymentFee}%` : formatCurrency(m.paymentFee));

/** 16c Top up rWallet. Phone: T3 form with summary footer. Landscape: 500px form | method tiles + summary. */
const TopupPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { selectedShop, user } = useAppSelector((s) => s.auth);
    const [accounts, setAccounts] = useState<PaymentAccount[]>([]);
    const [methods, setMethods] = useState<EPaymentMethod[]>([]);
    const [account, setAccount] = useState('');
    const [method, setMethod] = useState<EPaymentMethod | null>(null);
    const [amount, setAmount] = useState(0);
    const [email, setEmail] = useState(user?.email ?? '');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<TopupResponse | null>(null);
    const [accountSheet, setAccountSheet] = useState(false);

    useEffect(() => {
        if (!selectedShop) return;
        Promise.all([
            api.get<ApiResponse<PaymentAccount[]>>(API_ENDPOINTS.PAYMENT_ACCOUNTS(selectedShop.shopId)),
            api.get<ApiResponse<EPaymentMethod[]>>(API_ENDPOINTS.TOPUP_PAYMENT_METHODS(selectedShop.shopId)),
        ]).then(([a, m]) => {
            setAccounts(a.data.data);
            const main = a.data.data.find((x) => x.isMainAccount) ?? a.data.data[0];
            if (main) setAccount(main.identifier);
            setMethods(m.data.data.filter(isAllowed));
        }).catch((e) => setError(getErrorMessage(e))).finally(() => setLoading(false));
    }, [selectedShop]);

    const fee = useMemo(() => topupFee(method, amount), [method, amount]);
    const total = amount + fee;
    const canSubmit = amount >= MIN_TOPUP_AMOUNT && !!account && !!method && EMAIL_RE.test(email) && !submitting;

    if (!selectedShop) return null;

    const submit = async () => {
        if (!method || !canSubmit) return;
        setSubmitting(true);
        setError(null);
        try {
            const body: TopupRequest = {
                amount,
                paymentFee: fee,
                paymentMethod: toTopupMethod(method.paymentType),
                bankCode: method.paymentCode,
                email: email.trim(),
                accountIdentifier: account,
            };
            const res = await api.post<ApiResponse<TopupResponse>>(API_ENDPOINTS.TOPUP(selectedShop.shopId), body);
            setResult(res.data.data);
            toast.success(t('topup.created'));
        } catch (e) {
            setError(getErrorMessage(e));
        } finally {
            setSubmitting(false);
        }
    };

    const selectedAccount = accounts.find((a) => a.identifier === account);
    const amountError = amount > 0 && amount < MIN_TOPUP_AMOUNT ? t('topup.minAmount', { amount: formatCurrency(MIN_TOPUP_AMOUNT) }) : undefined;

    const accountRow = (
        <button type="button" onClick={() => setAccountSheet(true)} disabled={loading || accounts.length === 0} className="flex items-center gap-3 rounded-lg border border-line bg-surface-card px-3.5 py-3 text-left lg:px-4 lg:py-3.5">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brandsoft text-ink-brand lg:h-[42px] lg:w-[42px]"><Wallet className="h-[19px] w-[19px]" /></span>
            <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm font-extrabold lg:text-[15px]">{selectedAccount?.accountName ?? (loading ? t('common.loading') : t('topup.account'))}</span>
                {selectedAccount && <span className="text-xs text-ink-muted tnum">{selectedAccount.accountNumber}</span>}
            </span>
            {selectedAccount && <span className="text-[15px] font-black tnum text-ink-brand lg:text-[17px]">{formatCurrency(selectedAccount.balance)}</span>}
            <ChevronDown className="h-[18px] w-[18px] text-ink-muted" />
        </button>
    );

    const amountBlock = (
        <div className="flex flex-col gap-2">
            <span className="text-[13px] font-extrabold text-ink-secondary lg:text-[15px] lg:text-ink-primary">{t('topup.amount')}</span>
            <Input
                fieldSize="lg"
                numeric
                type="number"
                min={MIN_TOPUP_AMOUNT}
                step={10000}
                inputMode="numeric"
                placeholder="0"
                value={amount || ''}
                onChange={(e) => setAmount(Math.max(0, parseInt(e.target.value, 10) || 0))}
                error={amountError}
                leftIcon={<span className="text-xl font-extrabold text-ink-muted">Rp</span>}
                wrapperClassName="!h-[60px] !border-2 !border-accent-500 shadow-focus lg:!h-[68px]"
                className="!text-[26px] lg:!text-[30px]"
            />
            <div className="flex flex-wrap gap-1.5 lg:gap-2">
                {QUICK.map((v) => <Chip key={v} selected={amount === v} onClick={() => setAmount(v)}>{short(v)}</Chip>)}
            </div>
            <p className="text-xs text-ink-muted">{t('topup.helper', { min: formatCurrency(MIN_TOPUP_AMOUNT), email: email || '—' })}</p>
            <Input type="email" fieldSize="md" leftIcon={<Mail />} placeholder={t('topup.email')} value={email} onChange={(e) => setEmail(e.target.value)} error={email && !EMAIL_RE.test(email) ? t('topup.invalidEmail') : undefined} />
        </div>
    );

    const methodList = (layout: 'rows' | 'grid') => (
        <div className="flex flex-col gap-2">
            <span className="text-[13px] font-extrabold text-ink-secondary lg:text-[17px] lg:font-black lg:text-ink-primary">{t('checkout.paymentMethod')}</span>
            {methods.length === 0 && !loading && <Alert variant="warning" message={t('checkout.noPaymentMethods')} />}
            <div className={clsx(layout === 'grid' ? 'grid grid-cols-2 gap-2.5' : 'flex flex-col gap-2')}>
                {methods.map((m) => (
                    <MethodTile key={m.paymentCode} layout="inline" label={m.paymentName} sub={t('topup.fee', { fee: feeLabel(m) })} img={m.paymentImage} selected={method?.paymentCode === m.paymentCode} onClick={() => setMethod(m)} className="!h-[58px] lg:!h-[68px]" />
                ))}
            </div>
        </div>
    );

    const summary = (
        <div className="flex flex-col gap-[9px]">
            <SummaryLine label={t('topup.amount')} value={formatCurrency(amount)} />
            <SummaryLine label={method ? t('topup.feeLine', { method: method.paymentFeeUnit === 'PERCENT' ? `${method.paymentName} ${method.paymentFee}%` : method.paymentName }) : t('transactions.paymentFee')} value={formatCurrency(fee)} />
            <SummaryLine kind="total" label={t('topup.totalPaid')} value={formatCurrency(total)} />
        </div>
    );
    const cta = <Button variant="primary" size="lg" fullWidth onClick={submit} loading={submitting} disabled={!canSubmit}>{t('topup.submit')}</Button>;

    return (
        <>
            <div className="flex h-full min-h-0 flex-1 flex-col lg:hidden">
                <PageShell title={t('topup.title')} backTo="/settings" footer={<div className="flex flex-col gap-2.5">{summary}{cta}</div>}>
                    <div className="flex flex-col gap-3 pb-4">
                        {error && <Alert variant="error" message={error} />}
                        {accountRow}
                        {amountBlock}
                        {methodList('rows')}
                    </div>
                </PageShell>
            </div>

            <div className="hidden h-full min-h-0 flex-1 lg:flex">
                <PageShell title={t('topup.title')} backTo="/settings" className="!flex-none w-[500px] border-r border-line-subtle">
                    <div className="flex flex-col gap-3.5 pb-6">
                        {error && <Alert variant="error" message={error} />}
                        <span className="text-[15px] font-extrabold">{t('topup.account')}</span>
                        {accountRow}
                        {amountBlock}
                    </div>
                </PageShell>
                <div className="flex min-w-0 flex-1 flex-col gap-3.5 overflow-y-auto p-[22px_28px]">
                    {methodList('grid')}
                    <div className="flex-1" />
                    <div className="rounded-lg border border-line-subtle bg-surface-card p-4">{summary}</div>
                    {cta}
                </div>
            </div>

            <Sheet isOpen={accountSheet} onClose={() => setAccountSheet(false)} title={t('topup.account')} size="sm">
                <div className="flex flex-col gap-2">
                    {accounts.map((a) => {
                        const active = a.identifier === account;
                        return (
                            <button key={a.id} type="button" onClick={() => { setAccount(a.identifier); setAccountSheet(false); }} className={clsx('flex h-16 items-center gap-3 rounded-lg px-3.5 text-left', active ? 'border-2 border-primary-500 bg-brandsoft text-ink-brand' : 'border border-line bg-surface-card')}>
                                <span className={clsx('flex h-10 w-10 shrink-0 items-center justify-center rounded-full', active ? 'bg-surface-card' : 'bg-surface-sunken')}><Wallet className="h-[19px] w-[19px]" /></span>
                                <span className="flex min-w-0 flex-1 flex-col"><span className="truncate text-sm font-black">{a.accountName}</span><span className="text-xs font-bold text-ink-secondary tnum">{formatCurrency(a.balance)} · {a.accountNumber}</span></span>
                                {active && <CircleCheck className="h-5 w-5" />}
                            </button>
                        );
                    })}
                </div>
            </Sheet>

            {result && (
                <PendingPaymentView
                    isOpen
                    paymentUrl={result.paymentUrl}
                    invoiceIdentifier={result.transactionId}
                    amount={total}
                    methodName={method?.paymentName}
                    onClose={() => { setResult(null); navigate('/dashboard'); }}
                />
            )}
        </>
    );
};

export default TopupPage;
